import React, { Suspense, useEffect, useMemo } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { House } from 'lucide-react';
import { Content, FlamecoFrame, FlamecoLinkProps, FlamecoNavGroup, moduleForPath, useQuickAccess } from '../../flameco-ui';
import { TOOLS, groupTools } from '../../constants/tools';
import { FLAMECO_AUTH_BASE_URL } from '../../constants/flamecoAuth';
import { getToken } from '../../flameco/flameco-auth';
import { useAuth } from '../../contexts/AuthContext';
import { useRole } from '../../contexts/RoleContext';
import { ModuleId } from '../../types/auth';
import { Tool } from '../../types';

// The shared Flameco frame (top bar, app switcher, account menu, side nav) around
// every signed-in page of the web app. Not used in the TIA Android shell, on the
// sign-in callback or on print views (STANDARD.md section 3).

/** react-router link for the kit's frame, tiles and breadcrumbs. */
export const renderLink = ({ href, className, children, onClick, ...rest }: FlamecoLinkProps) => (
  <Link to={href} className={className} onClick={onClick} {...rest}>
    {children}
  </Link>
);

/** Favourites + Recent live on the person's Flameco account (customer-portal backend). */
const QUICK_SERVER = { apiBase: (process.env.REACT_APP_FLAMECO_SEARCH_API_URL || '').trim() || FLAMECO_AUTH_BASE_URL, getToken };

/** This person's Internal Tools favourites and recent tools (side nav and home page). */
export const useToolsQuickAccess = () => useQuickAccess('internal', QUICK_SERVER);

/** Tools this person may open: the app role (old matrix) AND the Flameco tag must both allow it. */
export function useVisibleTools(): Tool[] {
  const { can } = useRole();
  const { can: hasTag } = useAuth();
  return TOOLS.filter(t => can(t.id as ModuleId) && hasTag(t.tag));
}

/** Skeleton blocks while a tool's code loads. */
export const PageSkeleton: React.FC = () => (
  <Content>
    <div className="fl-skeleton" style={{ height: 32, width: 240, marginBottom: 8 }} />
    <div className="fl-skeleton" style={{ height: 20, width: 360, marginBottom: 24 }} />
    <div className="fl-skeleton" style={{ height: 320 }} />
  </Content>
);

const AppFrame: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { flamecoUser, signOut, can: hasTag } = useAuth();
  const tools = useVisibleTools();

  const quick = useToolsQuickAccess();
  const { visit } = quick;
  // Recent: every tool the person opens (nav, tile, link or address bar).
  const trackable = useMemo(
    () => tools.map(t => ({ key: t.id, href: t.id === 'threaded-inline-arrestors' ? '/flame-arrestors' : t.path })),
    [tools],
  );
  useEffect(() => {
    const t = moduleForPath(pathname, trackable) ?? (pathname.startsWith('/threaded-inline-arrestors') ? { key: 'threaded-inline-arrestors' } : null);
    if (t) visit(t.key);
  }, [pathname, trackable, visit]);

  const nav = useMemo<FlamecoNavGroup[]>(() => {
    const item = (t: Tool, keyPrefix = '') => {
      const Icon = t.icon;
      return {
        key: keyPrefix + t.id,
        label: t.name,
        href: t.path,
        icon: <Icon strokeWidth={2} />,
        // Flame Arrestors also answers on its old address.
        ...(t.id === 'threaded-inline-arrestors'
          ? { active: pathname.startsWith('/flame-arrestors') || pathname.startsWith('/threaded-inline-arrestors') }
          : {}),
      };
    };
    const favs = quick.favourites.map(id => tools.find(t => t.id === id)).filter((t): t is Tool => !!t);
    return [
      { items: [{ key: 'home', label: 'Home', href: '/', icon: <House strokeWidth={2} /> }] },
      ...(favs.length ? [{ section: 'Favorites', items: favs.map(t => item(t, 'fav-')) }] : []),
      ...groupTools(tools).map(g => ({ section: g.category.label, categoryHue: g.category.hue, items: g.tools.map(t => item(t)) })),
    ];
  }, [tools, pathname, quick.favourites]);

  // Doug (Ask Alpha), for people with the alpha.chat tag; search hands him what it can't find.
  const canDoug = hasTag('alpha.chat');
  const doug = useMemo(
    () =>
      canDoug
        ? {
            api: (process.env.REACT_APP_ALPHA_API_URL || '').trim() || 'https://agentbackend.loclx.io',
            widgetBase: (process.env.REACT_APP_ALPHA_WIDGET_BASE || '').trim() || undefined,
            app: 'internal-tools',
            appName: 'Flameco Internal Tools',
            getToken,
            navigate: (path: string) => navigate(path),
          }
        : undefined,
    [canDoug, navigate],
  );

  return (
    <FlamecoFrame
      app="internal"
      user={flamecoUser ? { name: flamecoUser.name, role: flamecoUser.role, employeeId: flamecoUser.employeeId } : null}
      onSignOut={() => void signOut({ everywhere: true })}
      nav={nav}
      currentPath={pathname}
      renderLink={renderLink}
      navigate={href => navigate(href)}
      search={{ apiBase: (process.env.REACT_APP_FLAMECO_SEARCH_API_URL || '').trim() || FLAMECO_AUTH_BASE_URL, getToken }}
      doug={doug}
      appUrls={process.env.REACT_APP_FLAMECO_APP_URLS}
    >
      <Suspense fallback={<PageSkeleton />}>
        <Outlet />
      </Suspense>
    </FlamecoFrame>
  );
};

export default AppFrame;
